/**
 * Company Details Page
 * Tela de visualização da empresa — segue a receita de `EmployeeDetailsPage`:
 * identidade (PageShell + IdentityBanner) → destaques e blocos temáticos
 * (CompanyDetailsTab) → relacionados (clínicas, responsáveis, contatos, CNAEs).
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
} from '@mui/material';
import {
  Edit as EditIcon,
  ArrowBack as BackIcon,
  Business as BusinessIcon,
  Badge as BadgeIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { PageShell, IdentityBanner, IdentifierChip } from '../../components';
import { CompanyStatusChip } from './components';
import {
  CompanyDetailsTab,
  CompanyProvidersTab,
  CompanyResponsiblesTab,
  CompanyContactsTab,
  CompanyCnaesTab,
  CompanyESocialTab,
} from './components/tabs';
import CompanyVaccinesTab from './components/tabs/CompanyVaccinesTab';
import CompanyEpisSection from './components/tabs/CompanyEpisSection';
import { Company, formatCNPJ } from '../../types/company';
import { companyService, type EsocialConfigAtual } from '../../services/companyService';
import { ApiRequestError } from '../../types/common';
import { mensagemDeFalha } from '../../utils/acessoNegado';
import { AvisoDeFalhaAoCarregar } from '../../components/AvisoDeFalhaAoCarregar';

const TEXTO_NAO_ENCONTRADA = 'Empresa não encontrada.';

const CompanyDetailsPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();

  const [company, setCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Id da empresa cujo erro já virou toast — o efeito de carga roda duas vezes em StrictMode.
  const idComToast = useRef<string | null>(null);

  // Load company data
  const loadCompany = async () => {
    if (!id) return;

    setLoading(true);
    setError(null);
    try {
      const data = await companyService.getCompanyById(id, false);
      setCompany(data);
      idComToast.current = null;
    } catch (err: unknown) {
      const status = err instanceof ApiRequestError ? err.status : undefined;
      if (status === 404) {
        setError(TEXTO_NAO_ENCONTRADA);
      } else {
        // 403 já tem o motivo na própria tela; só a falha genérica ganha toast.
        setError(mensagemDeFalha(err, 'Erro ao carregar empresa'));
        if (status !== 403 && idComToast.current !== id) {
          idComToast.current = id;
          enqueueSnackbar('Erro ao carregar empresa', { variant: 'error' });
        }
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCompany();
  }, [id]);

  // Scroll to section when ?tab= is present
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (!tab || loading) return;

    const el = document.getElementById(`section-${tab}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setSearchParams({}, { replace: true });
    }
  }, [loading, searchParams, setSearchParams]);

  // Mescla o que o servidor devolveu na empresa em memória, sem religar o `loading` da página.
  // A API devolve null para o que está vazio; `Company` o chama de ausente.
  const atualizarConfigEsocial = (mudancas: Partial<EsocialConfigAtual>) => {
    const { documentNumber, ecacExpirationDate, conditionStartDate, ...demais } = mudancas;
    const normalizadas: Partial<Company> = { ...demais };
    if ('documentNumber' in mudancas) normalizadas.documentNumber = documentNumber ?? undefined;
    if ('ecacExpirationDate' in mudancas) normalizadas.ecacExpirationDate = ecacExpirationDate ?? undefined;
    if ('conditionStartDate' in mudancas) normalizadas.conditionStartDate = conditionStartDate ?? undefined;
    setCompany((anterior) => (anterior ? { ...anterior, ...normalizadas } : anterior));
  };

  const baseBreadcrumbs = [
    { label: 'Início', path: '/' },
    { label: 'Empresas', path: '/companies' },
  ];

  const backButton = (
    <Button
      variant="outlined"
      size="small"
      startIcon={<BackIcon />}
      onClick={() => navigate('/companies')}
    >
      Voltar
    </Button>
  );

  if (loading) {
    return (
      <PageShell
        breadcrumbs={[...baseBreadcrumbs, { label: 'Carregando...' }]}
        actions={backButton}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      </PageShell>
    );
  }

  if (error || !company) {
    return (
      <PageShell breadcrumbs={[...baseBreadcrumbs, { label: 'Não foi possível abrir' }]}>
        <AvisoDeFalhaAoCarregar
          mensagem={error || TEXTO_NAO_ENCONTRADA}
          rotuloDoBotao="Voltar para empresas"
          aoVoltar={() => navigate('/companies')}
        />
      </PageShell>
    );
  }

  const companyId = company.id.toString();
  const companyName = company.tradeName || company.legalName;

  return (
    <PageShell
      breadcrumbs={[...baseBreadcrumbs, { label: companyName }]}
      actions={
        <>
          {backButton}
          <Button
            variant="contained"
            size="small"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/companies/${company.id}/edit`)}
          >
            Editar
          </Button>
        </>
      }
    >
      {/* 1. Identidade */}
      <IdentityBanner
        avatar={<BusinessIcon sx={{ fontSize: 32 }} />}
        name={companyName}
        status={<CompanyStatusChip status={company.status} />}
        context={
          <>
            Criado em{' '}
            {company.createdAt
              ? new Date(company.createdAt).toLocaleString('pt-BR')
              : '—'}
            {company.updatedAt &&
              ` · Atualizado em ${new Date(company.updatedAt).toLocaleString('pt-BR')}`}
          </>
        }
        identifiers={
          <IdentifierChip
            icon={<BadgeIcon sx={{ fontSize: 16 }} />}
            label={formatCNPJ(company.cnpj)}
          />
        }
      />

      {/* 2. Destaques + 3. Blocos temáticos */}
      <CompanyDetailsTab company={company} />

      {/* 4. Relacionados */}
      <div id="section-clinicas">
        <CompanyProvidersTab companyId={companyId} />
      </div>
      <div id="section-responsaveis">
        <CompanyResponsiblesTab companyId={companyId} />
      </div>
      <div id="section-contatos">
        <CompanyContactsTab companyId={companyId} />
      </div>
      <div id="section-cnaes">
        <CompanyCnaesTab companyId={companyId} />
      </div>
      <div id="section-vacinas">
        <CompanyVaccinesTab companyId={companyId} />
      </div>
      <div id="section-epis">
        <CompanyEpisSection companyId={companyId} />
      </div>

      {/* eSocial section */}
      <div id="section-esocial">
        <CompanyESocialTab
          companyId={companyId}
          esocialConfig={{
            documentType: company.documentType,
            documentNumber: company.documentNumber,
            ecacExpirationDate: company.ecacExpirationDate,
            esocialSendMode: company.esocialSendMode,
            conditionStartDate: company.conditionStartDate,
            procuracaoConfirmada: company.procuracaoConfirmada,
            procuracaoConfirmadaEm: company.procuracaoConfirmadaEm ?? undefined,
          }}
          onSalvo={atualizarConfigEsocial}
        />
      </div>
    </PageShell>
  );
};

export default CompanyDetailsPage;
